import { useEffect, useRef, useState } from 'react';
import bowser from 'yola-bowser';
import { utils } from '@yola/ws-sdk';
import throttle from 'lodash.throttle';

const isTouch = bowser.mobile || bowser.tablet || 'ontouchstart' in window;

const events = isTouch
  ? { start: 'touchstart', move: 'touchmove', end: 'touchend' }
  : { start: 'mousedown', move: 'mousemove', end: 'mouseup' };

function getClientCoords(event) {
  const source = isTouch ? event.changedTouches[0] : event;
  return [source.clientX, source.clientY];
}

function createDragHandler(callbacksRef, options) {
  return (startEvent) => {
    if (isTouch) {
      startEvent.preventDefault();
    }

    const [startX, startY] = getClientCoords(startEvent);

    callbacksRef.current.onStart(startEvent);

    function onMove(event) {
      const [x, y] = getClientCoords(event);
      callbacksRef.current.onMove([x - startX, y - startY], event);
    }

    const { throttleMove, throttleMoveDelay } = options;
    const moveCallback = throttleMove
      ? throttle(onMove, throttleMoveDelay, { trailing: true, leading: true })
      : onMove;

    function onEnd(event) {
      const [x, y] = getClientCoords(event);

      if (throttleMove) {
        moveCallback.flush();
        moveCallback.cancel();
      }

      callbacksRef.current.onEnd([x - startX, y - startY]);

      document.removeEventListener(events.move, moveCallback);
      document.removeEventListener(events.end, onEnd);
    }

    document.addEventListener(events.move, moveCallback);
    document.addEventListener(events.end, onEnd);
  };
}

// The duration of a frame in milliseconds (60 FPS)
const FRAME_DURATION = 16.67;

const defaultOptions = {
  throttleMove: false,
  throttleMoveDelay: FRAME_DURATION,
};

function useDrag(elementRefs, callbacks, options = defaultOptions) {
  const [elements, setElements] = useState([]);
  const callbacksRef = useRef({});

  // React doesn't allow to pass dynamic dependencies list to the hook deps. That why
  // we don't use useMemo and use useEffect to filter elements and set to the state.
  useEffect(() => {
    const acceptedElements = elementRefs
      .map((elementRef) => elementRef && elementRef.current)
      .filter((element) => element);

    setElements((currentElements) => {
      if (acceptedElements.length !== currentElements.length) {
        return acceptedElements;
      }

      if (acceptedElements.every((element, i) => element === currentElements[i])) {
        return currentElements;
      }

      return acceptedElements;
    });
    // We don't need any dependencies here because `elementsRefs` is passed as dynamic
    // value `useDrag([ref1, ref2], callbacks)`. So the reference will be different on
    // every render.
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  });

  // Use a ref to store the callbacks to avoid re-creating the handler
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (elements.length === 0) return utils.noop;

    const dragHandler = createDragHandler(callbacksRef, {
      throttleMove: options.throttleMove || defaultOptions.throttleMove,
      throttleMoveDelay: options.throttleMoveDelay || defaultOptions.throttleMoveDelay,
    });

    elements.forEach((element) => {
      element.addEventListener(events.start, dragHandler);
    });

    return () =>
      elements.forEach((element) => {
        element.removeEventListener(events.start, dragHandler);
      });
  }, [elements, options.throttleMove, options.throttleMoveDelay]);
}

export default useDrag;
